import React, { useState, useEffect, useMemo } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { AmbrSpotlightCard } from './AmbrSpotlightCard';
// import { PurchaseChart } from './PurchaseChart'; // re-enable with chart
import { DateRangePicker } from './DateRangePicker';
import { WarehouseAgeBadge } from './WarehouseAgeBadge';
import { ProductFilter } from './ProductFilter';
import { ProductTrendChart } from './ProductTrendChart';
import { useDailyPurchases } from '../../hooks/queries';

const COLORS = ['#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed', '#db2777'];



function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function Dashboard() {
  const { environment } = useEnvironment();
  const [startDate, setStartDate] = useState(() => daysAgo(30));
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);

  const { data: rawData, isLoading: _loading, error: queryError } = useDailyPurchases(startDate, endDate);
  const days = rawData?.days ?? [];
  const warehouseAge = rawData?.warehouseAge ?? null;
  const warehouseNotInitialised = queryError !== null && (queryError as any)?.status === 503;
  const error = !warehouseNotInitialised && queryError ? ((queryError as any)?.message ?? 'Failed to load dashboard data.') : null;

  const allProducts = useMemo(() => {
    // Accumulate total units per SKU across all days
    const volumeMap = new Map<string, { name: string; total: number }>();
    days.forEach(d => d.products.forEach(p => {
      const key = p.sku || `__name__${p.name}`;
      const entry = volumeMap.get(key);
      if (entry) { entry.total += p.count; }
      else { volumeMap.set(key, { name: p.name, total: p.count }); }
    }));

    // Deduplicate by normalized name — keep the SKU with the highest volume
    const byName = new Map<string, { sku: string; name: string; totalUnits: number }>();
    Array.from(volumeMap.entries()).forEach(([sku, { name, total }]) => {
      const norm = name.trim().toLowerCase();
      const existing = byName.get(norm);
      if (!existing || total > existing.totalUnits) {
        byName.set(norm, { sku, name, totalUnits: total });
      }
    });

    // Use product name as the stable key so selectedSkus survives date-range changes.
    // Different date windows can assign different BigCommerce SKUs to the same product
    // (when the same product has multiple SKUs with shifting volumes), which caused
    // the selection to reset on every period change. Name is always stable.
    return Array.from(byName.values())
      .sort((a, b) => b.totalUnits - a.totalUnits)
      .map(({ name, totalUnits }) => ({ sku: name, name, totalUnits }));
  }, [days]);

  const earliestDate = useMemo(() => {
    if (days.length === 0) { return undefined; }
    return days.map(d => d.date).sort()[0];
  }, [days]);

  useEffect(() => {
    const keysInRange = new Set(allProducts.map(p => p.sku)); // sku field = product name
    const kept = selectedSkus.filter(s => keysInRange.has(s));
    const ambrKey = allProducts.find(p => p.name.toUpperCase().includes('ASSOCIATE MEMBERSHIP'))?.sku;
    if (kept.length === 0 && ambrKey) {
      setSelectedSkus([ambrKey]);
    } else if (kept.length === 0 && allProducts.length > 0) {
      setSelectedSkus([allProducts[0].sku]);
    } else if (kept.length !== selectedSkus.length) {
      setSelectedSkus(kept);
    }
  }, [allProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  if (warehouseNotInitialised) {
    return (
      <div style={{ padding: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a2332', marginBottom: '1rem' }}>
          Purchase Dashboard
        </h1>
        <div
          data-testid="warehouse-not-initialised"
          style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            padding: '1.5rem',
            color: '#92400e',
          }}
        >
          <strong>Warehouse not yet initialised.</strong> Run the backfill Lambda to populate data:
          <pre style={{ marginTop: '0.75rem', fontSize: '0.85rem', backgroundColor: '#fff7ed', padding: '0.75rem', borderRadius: '4px' }}>
            {`aws lambda invoke --function-name bigcommerce-viewer-${environment}-warehouseBackfill /dev/null`}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a2332', margin: 0 }}>
          Purchase Dashboard
        </h1>
        {warehouseAge && <WarehouseAgeBadge warehouseAge={warehouseAge} />}
      </div>

      {/* Top row: AMBR spotlight + product selector + chart */}
      <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <AmbrSpotlightCard
          days={days}
          startDate={startDate}
          endDate={endDate}
          selectedNames={selectedSkus}
          colors={COLORS}
        />

        {/* Right column: product selector + date picker + trend chart */}
        {allProducts.length > 0 && (
          <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <ProductFilter
                products={allProducts}
                selected={selectedSkus}
                onChange={setSelectedSkus}
                colors={COLORS}
              />
            </div>
            {selectedSkus.length > 0 && days.length > 0 && (
              <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                {/* Date picker lives inside the chart card so it stays aligned */}
                <div style={{ marginBottom: '1rem' }}>
                  <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateChange} earliestDate={earliestDate} />
                </div>
                <ProductTrendChart days={days} selectedSkus={selectedSkus} colors={COLORS} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div
          data-testid="dashboard-error"
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1rem',
            color: '#991b1b',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Daily Purchases chart — temporarily hidden, improve locally before re-enabling
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginTop: 0, marginBottom: '1rem' }}>
          Daily Purchases
        </h2>
        <PurchaseChart days={days} loading={loading} />
      </div>
      */}
    </div>
  );
}
